/**
 * Vehicle identification number rules, mirroring `Core.Garage.VinValidator` on the server.
 *
 * The C# implementation is authoritative. This one exists so a typo is caught in the browser or on
 * a phone -- instantly, and offline -- instead of costing a round trip and coming back as a 400.
 * The two are held in agreement by the shared vectors in `fixtures/conformance/vin.json`, which
 * both test suites read.
 */
/** The length of a standardised VIN. */
export const VIN_LENGTH = 17;
/** Zero-based position of the check digit. */
const CHECK_DIGIT_INDEX = 8;
/** Positional weights from the standard. The check digit position itself carries weight 0. */
const WEIGHTS = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];
/**
 * Transliteration values for letters.
 *
 * I, O and Q are absent on purpose: they never appear in a VIN, because in print they cannot be
 * told apart from 1 and 0.
 */
const LETTER_VALUES = {
    A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8,
    J: 1, K: 2, L: 3, M: 4, N: 5, P: 7, R: 9,
    S: 2, T: 3, U: 4, V: 5, W: 6, X: 7, Y: 8, Z: 9,
};
/**
 * Normalises a VIN for storage and comparison.
 *
 * @param vin - The raw input.
 * @returns The trimmed, upper-cased value, or an empty string when nothing was supplied.
 */
export function normalizeVin(vin) {
    return vin === null || vin === undefined ? '' : vin.trim().toUpperCase();
}
/**
 * Determines whether a value has the right length and alphabet, ignoring the check digit.
 *
 * @param vin - The value to inspect.
 * @returns True when the value could be a post-1980 VIN.
 */
export function isWellFormedVin(vin) {
    const normalized = normalizeVin(vin);
    if (normalized.length !== VIN_LENGTH) {
        return false;
    }
    for (const character of normalized) {
        const isDigit = character >= '0' && character <= '9';
        if (!isDigit && !(character in LETTER_VALUES)) {
            return false;
        }
    }
    return true;
}
/**
 * Computes the expected check digit for a well-formed VIN.
 *
 * @param vin - A VIN of the correct length and alphabet.
 * @returns The expected ninth character: a digit, or `X` when the remainder is ten.
 * @throws {Error} The value is not a well-formed VIN.
 */
export function computeVinCheckDigit(vin) {
    const normalized = normalizeVin(vin);
    if (!isWellFormedVin(normalized)) {
        throw new Error('The value is not a well-formed 17-character VIN.');
    }
    let sum = 0;
    for (let index = 0; index < VIN_LENGTH; index++) {
        const character = normalized[index];
        const value = character >= '0' && character <= '9'
            ? character.charCodeAt(0) - '0'.charCodeAt(0)
            : LETTER_VALUES[character];
        sum += value * WEIGHTS[index];
    }
    const remainder = sum % 11;
    return remainder === 10 ? 'X' : String(remainder);
}
/**
 * Determines whether a VIN is well formed and its check digit is correct.
 *
 * @param vin - The VIN to validate.
 * @returns True when the VIN passes the check-digit test.
 * @remarks
 * A correct check digit proves the VIN was not mistyped. It does not prove the vehicle exists --
 * that is what the vPIC lookup is for.
 */
export function isValidVin(vin) {
    const normalized = normalizeVin(vin);
    return isWellFormedVin(normalized) && computeVinCheckDigit(normalized) === normalized[CHECK_DIGIT_INDEX];
}
//# sourceMappingURL=vin.js.map